import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  CircularProgress,
  Grid,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HeaderComponent from "../components/HeaderComponent";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Language from "../languages";
import Services from "../services/Services";

// 0=address, 1=easypoint, 2=inmall, 3=locker
function deliveryTypeInfoToMode(info) {
  if (!info) return 0;
  if (info.deliveryType === "address") return 0;
  if (info.deliveryType === "points" && info.type === "easypoint") return 1;
  if (info.deliveryType === "points" && info.type === "mall") return 2;
  if (info.deliveryType === "predefined") return 2;
  if (info.deliveryType === "postbox") return 3;
  return 0;
}

const inputSx = { mb: 2 };
const inputSize = "small";

export default function CodeFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const mall = useSelector((state) => state.mall);
  const language = useSelector((state) => state.language);

  const code = location.state?.code;
  const fromCode = location.state?.fromCode ?? false;

  const mallUri = localStorage.getItem("mallUri") || "";
  const mallTitle = (() => {
    try {
      const raw = localStorage.getItem("mall");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed[0]?.title || "" : "";
    } catch {
      return "";
    }
  })();

  const [loadingOrder, setLoadingOrder] = useState(!!code);
  const [orderID, setOrderID] = useState("");
  const [deliveryMode, setDeliveryMode] = useState(0);
  const [paymentType, setPaymentType] = useState("store");

  // mall delivery options from localStorage
  const [districtOptions, setDistrictOptions] = useState([]);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);
  const [localDeliveryData, setLocalDeliveryData] = useState({});
  const [postboxOptions, setPostboxOptions] = useState([]);
  const [epOptions, setEPOptions] = useState([]);
  const [mallPointOptions, setMallPointOptions] = useState([]);
  const [deliveryPrice, setDeliveryPrice] = useState(null);
  const [deliveryCurrency, setDeliveryCurrency] = useState("TRY");
  const [calculationUnit, setCalculationUnit] = useState("per-package");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [packageCount, setPackageCount] = useState("1");

  // address fields
  const [district, setDistrict] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [address, setAddress] = useState("");

  // easypoint fields
  const [selectedEPPoint, setSelectedEPPoint] = useState("");
  const [epNote, setEPNote] = useState("");

  // inmall fields
  const [selectedMallPoint, setSelectedMallPoint] = useState("");
  const [mallNote, setMallNote] = useState("");

  // locker fields
  const [selectedLocker, setSelectedLocker] = useState("");
  const [lockerNote, setLockerNote] = useState("");

  const [legalChecked, setLegalChecked] = useState(false);
  const [paymentChecked, setPaymentChecked] = useState(false);

  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");

  const [orderPosts, setOrderPosts] = useState([]);
  const [paymentToken, setPaymentToken] = useState("");
  const [conversationID, setConversationID] = useState("");
  const [htmlContent, setHtmlContent] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);

  // parse mall data from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("mall");
      if (!raw) return;
      const mallList = JSON.parse(raw);
      const m = Array.isArray(mallList) ? mallList[0] : null;
      if (!m) return;

      const dt = m.deliveryTypes;
      if (!dt) return;

      if (dt.localDelivery) {
        setDistrictOptions(Object.keys(dt.localDelivery));
        setLocalDeliveryData(dt.localDelivery);
      }

      if (Array.isArray(dt.postbox)) {
        setPostboxOptions(dt.postbox);
      }

      if (Array.isArray(dt.points)) {
        setEPOptions(dt.points.filter((p) => p.type === "easypoint"));
      }

      const inMallPoints = [
        ...(Array.isArray(dt.predefined) ? dt.predefined.filter((p) => p.type === "location") : []),
        ...(Array.isArray(dt.points) ? dt.points.filter((p) => p.type === "location") : []),
      ];
      setMallPointOptions(inMallPoints);

      if (m.calculationUnit) {
        setCalculationUnit(m.calculationUnit);
      }
    } catch {}
  }, []);

  // fetch order & pre-fill user info
  useEffect(() => {
    if (!code) return;

    const name = localStorage.getItem("name") || "";
    const lastname = localStorage.getItem("lastname") || "";
    const combined = `${name} ${lastname}`.trim();
    setFullName(combined);
    setEmail(localStorage.getItem("email") || "");
    setPhone(localStorage.getItem("phone") || "");
    setCardName(combined);

    const token = localStorage.getItem("token");
    Services.GetOrderWithCode(token, code)
      .then((res) => {
        if (res.data.status && res.data.result) {
          const result = res.data.result;
          setOrderID(result.orderID || "");
          setOrderPosts(result.posts || []);
          setDeliveryMode(deliveryTypeInfoToMode(result.deliveryTypeInfo));

          const firstPost = result.posts?.[0];
          if (firstPost?.ordersTotalPackageCount) {
            setPackageCount(firstPost.ordersTotalPackageCount);
          }
          setPaymentType(result.deliveryTypeInfo?.paymentType === "customer" ? "customer" : "store");
        }
      })
      .finally(() => setLoadingOrder(false));
  }, []);

  const totalAmount = (() => {
    if (!deliveryPrice) return null;
    const count =
      calculationUnit === "per-package" ? parseInt(packageCount) || 1 : 1;
    return (deliveryPrice * count).toFixed(2);
  })();

  const handleDistrictChange = (val) => {
    setDistrict(val);
    setNeighborhood("");
    const entry = localDeliveryData[val];
    setNeighborhoodOptions(entry?.neigbours || []);
    setDeliveryPrice(entry?.price ?? null);
    setDeliveryCurrency(entry?.currency || "TRY");
  };

  const handleEPSelect = (id) => {
    setSelectedEPPoint(id);
    const point = epOptions.find((p) => p.id === id);
    if (point) {
      setDeliveryPrice(point.price ?? null);
      setDeliveryCurrency(point.currency || "TRY");
    }
  };

  const handleLockerSelect = (id) => {
    setSelectedLocker(id);
    const box = postboxOptions.find((p) => p.id === id);
    if (box) {
      setDeliveryPrice(box.price ?? null);
      setDeliveryCurrency(box.currency || "TRY");
    }
  };

  const handleMallPointSelect = (val) => {
    setSelectedMallPoint(val);
    try {
      const item = JSON.parse(val);
      setDeliveryPrice(item.price ?? null);
      setDeliveryCurrency(item.currency || "TRY");
    } catch {}
  };

  const isEmailFormatValid = (val) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const isFormValid = () => {
    if (!fullName.trim() || !email.trim() || !phone.trim() || !legalChecked)
      return false;
    if (!isEmailFormatValid(email)) return false;
    if (paymentType === "customer" && !paymentChecked) return false;
    if (paymentType === "customer" && !deliveryPrice) return false;
    if (deliveryMode === 0 && (!district || !neighborhood)) return false;
    if (deliveryMode === 1 && !selectedEPPoint) return false;
    if (deliveryMode === 2 && !selectedMallPoint) return false;
    if (deliveryMode === 3 && !selectedLocker) return false;
    return true;
  };

  const isPaymentValid = () =>
    cardName.trim() &&
    cardNumber.replace(/\D/g, "").length >= 15 &&
    cardExpiry.length === 5 &&
    cardCvv.trim().length >= 3;

  const handleExpiryChange = (e) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length >= 2) {
      let month = val.slice(0, 2);
      if (parseInt(month, 10) > 12) month = "12";
      val = month + (val.length > 2 ? `/${val.slice(2, 4)}` : "");
    }
    setCardExpiry(val);
  };

  const buildDeliveryTypeInfo = () => {
    if (deliveryMode === 0) {
      return {
        deliveryType: "address",
        type: "custom",
        text: `${district} ${neighborhood} ${address}`.trim(),
        additionalNote: "",
      };
    }
    if (deliveryMode === 1) {
      const point = epOptions.find((p) => p.id === selectedEPPoint);
      return {
        deliveryType: "points",
        type: "easypoint",
        id: selectedEPPoint,
        text: point?.title || "",
        additionalNote: epNote,
        price: point?.price ?? null,
        currency: point?.currency || "TRY",
      };
    }
    if (deliveryMode === 2) {
      let item = null;
      try { item = JSON.parse(selectedMallPoint); } catch {}
      return {
        deliveryType: "predefined",
        type: item?.type || "location",
        text: item?.title || "",
        id: item?.id || "",
        additionalNote: mallNote,
        price: item?.price ?? null,
        currency: item?.currency || "TRY",
      };
    }
    if (deliveryMode === 3) {
      const box = postboxOptions.find((p) => p.id === selectedLocker);
      return {
        deliveryType: "postbox",
        id: selectedLocker,
        text: box?.title || "",
        additionalNote: lockerNote,
        price: box?.price ?? null,
        currency: box?.currency || "TRY",
      };
    }
    return {};
  };

  const getNameParts = () => {
    const parts = fullName.trim().split(" ");
    return {
      firstName: parts.slice(0, -1).join(" ") || parts[0] || "",
      lastName: parts[parts.length - 1] || "",
    };
  };

  const handleContinue = () => {
    if (paymentType === "customer") {
      setShowPaymentStep(true);
      Services.GetTokenForPayment().then((res) => {
        if (res.data?.result?.token) setPaymentToken(res.data.result.token);
      });
      return;
    }

    const token = localStorage.getItem("token");
    const customerID = localStorage.getItem("customerID") || "";
    const { firstName, lastName } = getNameParts();

    setSubmitLoading(true);
    setErrorMessage("");
    Services.UpdateOrderAndPostByCustomer(
      token,
      orderID,
      customerID,
      firstName,
      lastName,
      phone,
      email,
      buildDeliveryTypeInfo(),
    )
      .then(() => setShowSuccess(true))
      .catch(() => setErrorMessage("İşlem sırasında bir hata oluştu, lütfen tekrar deneyin."))
      .finally(() => setSubmitLoading(false));
  };

  const handlePaymentSubmit = () => {
    setPaymentLoading(true);
    setErrorMessage("");

    const customerID = localStorage.getItem("customerID") || "";
    const convID = `HDF-${Date.now()}${customerID}`;
    setConversationID(convID);

    const { firstName, lastName } = getNameParts();
    const [expMonth, expYear] = cardExpiry.split("/");
    const creditCardInfos = {
      holderName: cardName.trim(),
      cardNumber: cardNumber.replace(/\D/g, ""),
      expMonth,
      expYear,
      cvc: cardCvv,
    };
    const customer = { name: firstName, lastname: lastName, phone, email };
    const amount = totalAmount ? parseFloat(totalAmount) : 0;
    const itemsArray = [{ name: `${packageCount} adet Handsfree Paketi`, price: amount }];

    Services.Payment(paymentToken, convID, amount, customer, creditCardInfos, itemsArray)
      .then((res) => {
        if (res.data.status) {
          setHtmlContent(res.data.result);
        } else {
          setErrorMessage("Ödeme başlatılamadı, lütfen tekrar deneyin.");
        }
      })
      .catch(() => setErrorMessage("Ödeme sırasında bir hata oluştu."))
      .finally(() => setPaymentLoading(false));
  };

  useEffect(() => {
    if (!conversationID || !paymentToken) return;

    const interval = setInterval(() => {
      Services.CheckPayment(paymentToken, conversationID).then((res) => {
        const status = res.data.result;

        if (status === "1") {
          clearInterval(interval);
          const token = localStorage.getItem("token");
          const customerID = localStorage.getItem("customerID") || "";
          const { firstName, lastName } = getNameParts();

          const setPaidPromises = orderPosts.map((post) =>
            Services.SetPaid(token, conversationID, "online", post.postID),
          );

          Promise.all(setPaidPromises)
            .then(() =>
              Services.UpdateOrderAndPostByCustomer(
                token,
                orderID,
                customerID,
                firstName,
                lastName,
                phone,
                email,
                buildDeliveryTypeInfo(),
              ),
            )
            .finally(() => { setHtmlContent(null); setShowSuccess(true); });
        } else if (status === "2") {
          clearInterval(interval);
          setHtmlContent(null);
          Services.GetErrorPayment(paymentToken, conversationID)
            .then((errorRes) => {
              setErrorMessage(
                errorRes.data?.result?.errors?.errorDescription ||
                  "Ödeme başarısız, lütfen tekrar deneyin.",
              );
            })
            .catch(() => setErrorMessage("Ödeme başarısız."));
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [conversationID, paymentToken]);

  const formTitle =
    paymentType === "customer"
      ? Language(mall, language, "customerPaidOrderForm")
      : Language(mall, language, "storePaidOrderForm");

  if (loadingOrder) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4, mx: 2 }}>
        <HeaderComponent
          left="back"
          middle="logo"
          right="icon"
          goBack={() => {
            if (showPaymentStep) setShowPaymentStep(false);
            else if (fromCode) navigate(mallUri ? `/${mallUri}` : "/");
            else navigate(-1);
          }}
        />

        <Box sx={{ mt: 4 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              textAlign: "center",
              mb: mallTitle ? 1 : 3,
              color: "rgba(30,30,30,1)",
            }}
          >
            {formTitle}
          </Typography>

          {mallTitle ? (
            <Typography
              sx={{
                fontSize: 12,
                textAlign: "center",
                mb: 3,
                color: "rgba(117,117,117,1)",
              }}
            >
              {mallTitle}
            </Typography>
          ) : null}

          {!showPaymentStep ? (
            <>
              <TextField
                label={Language(mall, language, "fullName")}
                fullWidth
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                size={inputSize}
                sx={inputSx}
              />

              <TextField
                label={Language(mall, language, "emailLabel")}
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size={inputSize}
                sx={inputSx}
                error={email.trim().length > 0 && !isEmailFormatValid(email)}
                helperText={
                  email.trim().length > 0 && !isEmailFormatValid(email)
                    ? Language(mall, language, "invalidEmail")
                    : undefined
                }
              />

              <TextField
                label={Language(mall, language, "phone")}
                fullWidth
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputProps={{ inputMode: "tel" }}
                size={inputSize}
                sx={inputSx}
              />

              <TextField
                label={Language(mall, language, "packageCount")}
                fullWidth
                value={packageCount}
                disabled
                size={inputSize}
                sx={inputSx}
              />

              {/* ADDRESS */}
              {deliveryMode === 0 && (
                <>
                  <FormControl fullWidth size={inputSize} sx={inputSx}>
                    <InputLabel>
                      {Language(mall, language, "district")}
                    </InputLabel>
                    <Select
                      value={district}
                      label={Language(mall, language, "district")}
                      onChange={(e) => handleDistrictChange(e.target.value)}
                    >
                      {districtOptions.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    fullWidth
                    size={inputSize}
                    sx={inputSx}
                    disabled={!district}
                  >
                    <InputLabel>
                      {Language(mall, language, "town")}
                    </InputLabel>
                    <Select
                      value={neighborhood}
                      label={Language(mall, language, "town")}
                      onChange={(e) => setNeighborhood(e.target.value)}
                    >
                      {neighborhoodOptions.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={Language(mall, language, "enterFullAddress")}
                    fullWidth
                    multiline
                    rows={3}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    size={inputSize}
                    sx={inputSx}
                  />
                </>
              )}

              {/* EASYPOINT */}
              {deliveryMode === 1 && (
                <>
                  <FormControl fullWidth size={inputSize} sx={inputSx}>
                    <InputLabel>
                      {Language(mall, language, "epPoint")}
                    </InputLabel>
                    <Select
                      value={selectedEPPoint}
                      label={Language(mall, language, "epPoint")}
                      onChange={(e) => handleEPSelect(e.target.value)}
                    >
                      {epOptions.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={Language(mall, language, "deliveryPointNote")}
                    fullWidth
                    multiline
                    rows={3}
                    value={epNote}
                    onChange={(e) => setEPNote(e.target.value)}
                    size={inputSize}
                    sx={inputSx}
                  />
                </>
              )}

              {/* IN-MALL */}
              {deliveryMode === 2 && (
                <>
                  <FormControl fullWidth size={inputSize} sx={inputSx}>
                    <InputLabel>
                      {Language(mall, language, "mallPoint")}
                    </InputLabel>
                    <Select
                      value={selectedMallPoint}
                      label={Language(mall, language, "mallPoint")}
                      onChange={(e) => handleMallPointSelect(e.target.value)}
                    >
                      {mallPointOptions.map((item) => (
                        <MenuItem key={item.id || item.title} value={JSON.stringify(item)}>
                          {item.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={Language(mall, language, "deliveryPointNote")}
                    fullWidth
                    multiline
                    rows={3}
                    value={mallNote}
                    onChange={(e) => setMallNote(e.target.value)}
                    size={inputSize}
                    sx={inputSx}
                  />
                </>
              )}

              {/* LOCKER */}
              {deliveryMode === 3 && (
                <>
                  <FormControl fullWidth size={inputSize} sx={inputSx}>
                    <InputLabel>
                      {Language(mall, language, "lockerPoint")}
                    </InputLabel>
                    <Select
                      value={selectedLocker}
                      label={Language(mall, language, "lockerPoint")}
                      onChange={(e) => handleLockerSelect(e.target.value)}
                    >
                      {postboxOptions.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={Language(mall, language, "deliveryPointNote")}
                    fullWidth
                    multiline
                    rows={3}
                    value={lockerNote}
                    onChange={(e) => setLockerNote(e.target.value)}
                    size={inputSize}
                    sx={inputSx}
                  />
                </>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={legalChecked}
                    onChange={(e) => setLegalChecked(e.target.checked)}
                    size="small"
                    sx={{
                      color: "#FF6E60",
                      "&.Mui-checked": { color: "#FF6E60" },
                      pt: 0,
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12 }}>
                    <span
                      style={{
                        color: "#FF6E60",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      {Language(mall, language, "legalConsentLink")}
                    </span>{" "}
                    {Language(mall, language, "legalConsentSuffix")}
                  </Typography>
                }
                sx={{
                  mb: paymentType === "customer" ? 1 : 3,
                  alignItems: "flex-start",
                }}
              />

              {paymentType === "customer" && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={paymentChecked}
                      onChange={(e) => setPaymentChecked(e.target.checked)}
                      size="small"
                      sx={{
                        color: "#FF6E60",
                        "&.Mui-checked": { color: "#FF6E60" },
                        pt: 0,
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12 }}>
                      {Language(
                        mall,
                        language,
                        "paymentAmountConsent",
                        totalAmount
                          ? `${totalAmount} ${deliveryCurrency}`
                          : "—",
                      )}
                    </Typography>
                  }
                  sx={{ mb: 3, alignItems: "flex-start" }}
                />
              )}

              {errorMessage ? (
                <Typography sx={{ color: "error.main", fontSize: 13, mb: 2, textAlign: "center" }}>
                  {errorMessage}
                </Typography>
              ) : null}

              <Button
                variant="contained"
                color="newRed"
                fullWidth
                disabled={!isFormValid() || submitLoading}
                sx={{
                  paddingY: 1.5,
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  borderRadius: 2,
                  mb: 4,
                }}
                onClick={handleContinue}
              >
                {submitLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  Language(mall, language, "continue")
                )}
              </Button>
            </>
          ) : (
            /* PAYMENT STEP */
            <Box sx={{ mt: 1 }}>
              {htmlContent ? (
                <iframe
                  srcDoc={htmlContent}
                  style={{
                    width: "100vw",
                    height: "100vh",
                    border: "none",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    zIndex: 9999,
                  }}
                  title="3D Secure"
                />
              ) : (
                <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 3 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
                    {Language(mall, language, "completePaymentTitle")}
                  </Typography>

                  <Typography sx={{ fontSize: 12, color: "gray", mb: 2 }}>
                    {Language(mall, language, "cardDetails")}
                  </Typography>

                  <TextField
                    fullWidth
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    placeholder={Language(mall, language, "cardOwner")}
                    size={inputSize}
                    sx={inputSx}
                  />

                  <TextField
                    fullWidth
                    value={cardNumber}
                    onChange={(e) =>
                      setCardNumber(e.target.value.replace(/\D/g, "").slice(0, 16))
                    }
                    inputProps={{ inputMode: "numeric", maxLength: 16 }}
                    placeholder={Language(mall, language, "cardNumber")}
                    size={inputSize}
                    sx={inputSx}
                  />

                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        value={cardExpiry}
                        onChange={handleExpiryChange}
                        inputProps={{ maxLength: 5 }}
                        placeholder="MM/YY"
                        size={inputSize}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        value={cardCvv}
                        onChange={(e) =>
                          setCardCvv(e.target.value.replace(/\D/g, "").slice(0, 4))
                        }
                        inputProps={{ inputMode: "numeric", maxLength: 4 }}
                        placeholder="CVV"
                        size={inputSize}
                      />
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {Language(mall, language, "totalAmount")}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      {totalAmount ? `${totalAmount} ${deliveryCurrency}` : "—"}
                    </Typography>
                  </Box>

                  {errorMessage ? (
                    <Typography sx={{ color: "error.main", fontSize: 13, mb: 2, textAlign: "center" }}>
                      {errorMessage}
                    </Typography>
                  ) : null}

                  <Button
                    variant="contained"
                    color="newRed"
                    fullWidth
                    disabled={!isPaymentValid() || paymentLoading}
                    sx={{
                      paddingY: 1.5,
                      fontWeight: 700,
                      fontSize: 14,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                    onClick={handlePaymentSubmit}
                  >
                    {paymentLoading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      Language(mall, language, "completePayment")
                    )}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={showSuccess}
        onClose={() => navigate(mallUri ? `/${mallUri}` : "/")}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogContent sx={{ textAlign: "center", py: 5, px: 4 }}>
          <Typography
            sx={{ fontSize: 16, fontWeight: 700, mb: 1, lineHeight: 1.4 }}
          >
            {Language(mall, language, "transactionSuccess")}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "gray", mb: 3 }}>
            {Language(mall, language, "processSuccessMessage")}
          </Typography>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#4CAF50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <CheckCircleIcon sx={{ color: "white", fontSize: 40 }} />
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 3 }}>
            {Language(mall, language, "thankYou")}
          </Typography>
          <Button
            variant="contained"
            color="newRed"
            fullWidth
            sx={{ paddingY: 1.2, fontWeight: 700, textTransform: "none", borderRadius: 2 }}
            onClick={() => navigate(mallUri ? `/${mallUri}` : "/")}
          >
            {Language(mall, language, "returnToHome")}
          </Button>
        </DialogContent>
      </Dialog>
    </Container>
  );
}
